import {
  AlertFilled,
  CaretDownOutlined,
  CaretUpOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  FileTextFilled,
  FireFilled,
  FrownFilled,
  HeartFilled,
  MedicineBoxFilled,
  MehFilled,
  PlusOutlined,
  RightOutlined,
  SmileFilled,
  ThunderboltFilled,
  WarningFilled,
} from '@ant-design/icons';
import { clearCache, useRequest } from 'ahooks';
import { Alert, Collapse, Form, Input, InputNumber, Modal, Spin, Typography } from 'antd';
import ColorHash from 'color-hash-ts';
import { SEXUAL_ACTIVITY_COLORS } from 'components/charts/SexualActivityChart';
import { VAGINAL_DISCHARGE_COLORS } from 'components/charts/VaginalDischargesColors';
import SymptomTrackingReminder from 'components/SymptomTrackingReminder';
import { Button } from 'components/ui';
import { Dayjs } from 'dayjs';
import { colors, palette, radius, symptomSeverity } from 'design';
import { usePreferenceQuery } from 'hooks/usePreferenceQuery';
import usePreferences from 'hooks/usePreferences';
import useShouldAskForPeriod from 'hooks/useShouldAskForPeriod';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { usePushNotificationOptIn } from 'push/PushNotificationOptInContext';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import ApiSymptomTrackingManager from 'services/api/ApiSymptomTrackingManager';
import Platform from 'utils/Platform';
import Drop from '../assets/drop.svg?react';
import Signal0 from '../assets/signal-0.svg?react';
import Signal1 from '../assets/signal-1.svg?react';
import Signal2 from '../assets/signal-2.svg?react';
import Signal3 from '../assets/signal-3.svg?react';

const colorHash = new ColorHash();

const { Title } = Typography;

const QUESTION_ICONS = {
  period: (
    <Drop
      fill={colors.dataViz.veryBad}
      style={{ width: '1em', height: '1em', verticalAlign: '-0.125em', display: 'inline-block' }}
    />
  ),
  generalState: <SmileFilled style={{ color: colors.dataViz.veryGood }} />,
  pain: <AlertFilled style={{ color: colors.dataViz.bad }} />,
  symptoms: <MedicineBoxFilled style={{ color: colors.dataViz.average }} />,
  vaginalDischarges: (
    <Drop
      fill={palette.teal[300]}
      style={{ width: '1em', height: '1em', verticalAlign: '-0.125em', display: 'inline-block' }}
    />
  ),
  sexualActivity: <HeartFilled style={{ color: colors.brand.highlight }} />,
  bodyTemperature: <FireFilled style={{ color: colors.brand.highlightStrong }} />,
  notes: <FileTextFilled style={{ color: colors.text.tertiary }} />,
};

const useStyles = createUseStyles({
  modalContent: {
    maxHeight: '70vh',
    overflowY: 'auto',
    scrollbarWidth: 'none',

    // Toggle-style answer buttons only: `data-selected` is set whenever the
    // shared Button's `selected` prop is passed, which the plain CTA/link
    // buttons elsewhere on this page never do, so they're untouched.
    //
    // `!important`: antd's theme puts a pill radius on every button, and the
    // selected state adds its own inline `radius.md` ring on top of that —
    // both need beating so selected and unselected read as the same shape.
    '& button[data-selected]': {
      borderRadius: `${radius.lg}px !important`,
    },
    // Same fill as the "Catégories suivies" panel below, not the app-wide
    // default button background (`surface.sunken`) — so the answer buttons
    // read as belonging to this survey rather than as generic app buttons.
    // Scoped to unselected buttons: selected ones keep their own accent fill.
    // `!important` for the same reason as the radius rule above.
    '& button[data-selected="false"]': {
      backgroundColor: `${colors.surface.page} !important`,
    },
  },
  surveySection: {
    justifyContent: 'flex-start',
    textAlign: 'left',
    marginBottom: 6,

    '& h5,p': {
      color: colors.text.primary,
      margin: '24px 0 4px 0',
      paddingLeft: 1,
    },
  },
  titleEmphasis: {
    color: colors.brand.accentStrong,
    fontWeight: 'bold',
  },
  answersContainer: {
    display: 'flex',
    maxWidth: '100%',
    margin: 'auto',
    flexDirection: 'column',
    gap: 12,

    '& h5': {
      color: colors.text.primary,
      marginTop: 16,
    },
  },
  horizontalAnswersWrapper: {
    position: 'relative',

    // Right fade overlay
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: 20,
      height: '100%',
      background: 'linear-gradient(to left, white, transparent)',
      pointerEvents: 'none',
      zIndex: 2,
    },
  },

  horizontalAnswersContainer: {
    display: 'flex',
    overflowX: 'auto',
    gap: 12,
    padding: '2px 15px 10px 2px',
    scrollbarWidth: 'none', // Firefox
    msOverflowStyle: 'none', // IE and Edge

    '&::-webkit-scrollbar': {
      display: 'none', // Chrome, Safari and Opera
    },

    '& button': {
      minWidth: 85,
      width: 85,
      height: 80,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 8,
      textAlign: 'center',
      fontSize: 12,
      lineHeight: 1.2,
      whiteSpace: 'normal',
      wordBreak: 'normal',
      overflowWrap: 'break-word',
      hyphens: 'auto',
    },
    '& .anticon': {
      fontSize: 20,
      marginBottom: 4,
    },
  },

  twoRowsHorizontalAnswersContainer: {
    composes: '$horizontalAnswersContainer',
    display: 'grid',
    gridTemplateRows: 'repeat(2, 1fr)',
    gridAutoFlow: 'column',
    gridAutoColumns: 'minmax(80px, max-content)',
    rowGap: 16,
  },
  noPeriodsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,

    '& button': {
      padding: 0,
    },

    '& p': {
      margin: 0,
    },
  },

  infoText: {
    color: colors.text.primary,
    textAlign: 'left',
    '& button': {
      marginTop: 15,
    },
  },
  freeTextFieldForm: {
    width: '100%',
    marginBottom: 16,
    '& textarea': {
      backgroundColor: colors.surface.page,
      height: 150,
      border: `1px solid ${colors.border.subtle}`,
      borderRadius: radius.lg,
    },
    '& .ant-form-item': {
      marginBottom: 0,
    },
    '& .ant-form': {
      marginBottom: 0,
    },
  },
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  fixedBottomButton: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: 'white',
    padding: '16px 0',
    borderTop: `1px solid ${colors.border.subtle}`,
    marginTop: 'auto',
  },
  verticalSpace: {
    height: 16,
  },
  reminderSection: {
    marginBottom: 32,
    padding: 20,
    borderRadius: 8,
  },
  symptomDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  collapseCategories: {
    '& .ant-collapse-item': {
      border: 'none',
    },
    '& .ant-collapse-header': {
      borderBottom: 'none',
    },
    '& .ant-collapse-header-text': {
      order: 1,
      flex: 1,
    },
    '& .ant-collapse-expand-icon': {
      order: 2,
      paddingInlineEnd: '0',
    },
    '& .ant-collapse-content': {
      border: 'none',
      backgroundColor: colors.surface.page,
    },
    '& .ant-collapse-content-box': {
      borderTop: 'none',
    },
    '& .ant-collapse-panel, & .ant-collapse-panel-active': {
      border: 'none',
    },
  },
  questionSelectorWrapper: {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: 56,
      background: `linear-gradient(to left, ${colors.surface.page} 0%, transparent 100%)`,
      pointerEvents: 'none',
      zIndex: 1,
    },
  },
  questionSelectorContainer: {
    display: 'flex',
    overflowX: 'auto',
    overflowY: 'visible',
    gap: 8,
    padding: '3px 56px 3px 3px',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '& button': {
      whiteSpace: 'nowrap',
      flexShrink: 0,
    },
    '& button[data-selected="true"]': {
      background: 'white',
    },
    '& button[data-selected="false"]': {
      color: colors.text.tertiary,
      outline: `2px solid ${colors.border.default}`,
    },
    '& button[data-selected="false"] .anticon': {
      color: colors.text.tertiary,
    },
    '& button[data-selected="false"] svg': {
      fill: colors.text.tertiary,
    },
  },
  fertilityStatusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  fertilityDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  fertilityDotFertileHollow: {
    composes: '$fertilityDot',
    border: `2px solid ${palette.teal[500]}`,
    background: 'transparent',
  },
  fertilityDotOvulationHollow: {
    composes: '$fertilityDot',
    border: `2px solid ${palette.teal[900]}`,
    background: 'transparent',
  },
  fertilityDotFertileFilled: {
    composes: '$fertilityDot',
    backgroundColor: palette.teal[500],
  },
  fertilityDotOvulationFilled: {
    composes: '$fertilityDot',
    backgroundColor: palette.teal[900],
  },
});

interface SymptomTrackingSurveyModalProps {
  open: boolean;
  onClose: () => void;
  date: Dayjs;
}

export default function SymptomTrackingSurveyModal({ open, onClose, date }: SymptomTrackingSurveyModalProps) {
  // Hooks
  const { t } = useTranslation();
  const styles = useStyles();

  const { getSpecificDailyStatus, refresh, hasTrackingBeenUsed, getFertilityStatusForDay } = useSymptomTrackingData();
  const wasPeriodQuickfilled = getSpecificDailyStatus(date)?.menstruation_amount;

  const isEditing = getSpecificDailyStatus(date) !== undefined;

  const [extraSymptomsForm] = Form.useForm();
  const extraSymptomsValue = Form.useWatch('symptoms', extraSymptomsForm);

  const [freeNotesForm] = Form.useForm();
  const freeNotes = Form.useWatch('notes', freeNotesForm);

  const [survey, setSurvey] = useState({
    date: date.format('YYYY-MM-DD'),
    general_state: null as number | null,
    menstruation_amount: wasPeriodQuickfilled || 0,
    pain_level: null as number | null,
    symptoms: new Set<string>([]),
    vaginal_discharges: new Set<string>([]),
    sexual_activity: null as string | null,
    body_temperature: null as number | null,
  });

  const [showExtraSymptomsField, setShowExtraSymptomsField] = useState(false);
  const [showSkipPeriodWarning, setShowSkipPeriodWarning] = useState(false);
  const [showReminderPage, setShowReminderPage] = useState(false);

  const { requestOptIn } = usePushNotificationOptIn();

  const { value: reminderTime, loading: loadingReminderTime } = usePreferences(Keys.TRACKING_REMINDER_TIME);
  const areNotificationsEnabled = reminderTime.hour !== undefined;

  const { value: shouldAskAgainForReminder, set: setShouldAskForReminder } = usePreferences(
    Keys.TRACKING_SURVEY_SHOULD_ASK_FOR_REMINDER,
  );
  const shouldAskForReminder = shouldAskAgainForReminder !== false && !areNotificationsEnabled && Platform.isNative();

  const { isPeriodTrackingEnabled, setShouldAskForPeriod, loadingShouldAskForPeriod } = useShouldAskForPeriod();

  const defaultVisibleQuestions = [
    'generalState',
    'pain',
    'symptoms',
    'vaginalDischarges',
    'sexualActivity',
    'bodyTemperature',
    'notes',
  ];
  const { value: visibleQuestions, set: setVisibleQuestions } = usePreferenceQuery<string[]>(
    Keys.TRACKING_SURVEY_VISIBLE_QUESTIONS,
    defaultVisibleQuestions,
    open,
  );
  const { value: categoriesOpen, set: setCategoriesOpen } = usePreferenceQuery<boolean>(
    Keys.TRACKING_SURVEY_CATEGORIES_OPEN,
    true,
    open,
  );

  const questionOptions = useMemo(
    () => [
      { label: t('symptomTracking.survey.howAreYouToday'), value: 'generalState', icon: QUESTION_ICONS.generalState },
      { label: t('symptomTracking.survey.pain.painQuestion'), value: 'pain', icon: QUESTION_ICONS.pain },
      { label: t('symptomTracking.survey.symptoms.otherSymptoms'), value: 'symptoms', icon: QUESTION_ICONS.symptoms },
      {
        label: t('symptomTracking.survey.vaginalDischarges.title'),
        value: 'vaginalDischarges',
        icon: QUESTION_ICONS.vaginalDischarges,
      },
      {
        label: t('symptomTracking.survey.sexualActivity.title'),
        value: 'sexualActivity',
        icon: QUESTION_ICONS.sexualActivity,
      },
      {
        label: t('symptomTracking.survey.bodyTemperature.title'),
        value: 'bodyTemperature',
        icon: QUESTION_ICONS.bodyTemperature,
      },
      { label: t('symptomTracking.survey.addComment'), value: 'notes', icon: QUESTION_ICONS.notes },
    ],
    [t],
  );

  function toggleQuestion(questionValue: string) {
    const isCurrentlySelected = visibleQuestions.includes(questionValue);
    const newSelection = isCurrentlySelected
      ? visibleQuestions.filter(q => q !== questionValue)
      : [...visibleQuestions, questionValue];
    if (newSelection.length === 0) return;
    setVisibleQuestions(newSelection);
    posthog.capture('tracking.survey.questions_selection_changed', { selectedQuestions: newSelection });
  }

  const { run: loadSurvey, loading: isLoadingSurveyData } = useRequest(
    () => ApiSymptomTrackingManager.getSpecificDailyStatus(date.format('YYYY-MM-DD')),
    {
      manual: true,
      onSuccess: data => {
        setSurvey({
          date: data.date as string,
          general_state: data.general_state ?? null,
          menstruation_amount: data.menstruation_amount ?? 0,
          pain_level: data.pain_level ?? null,
          symptoms: new Set(data.symptoms),
          vaginal_discharges: new Set(data.vaginal_discharges),
          sexual_activity: data.sexual_activity ?? null,
          body_temperature: data.body_temperature ?? null,
        });
        freeNotesForm.setFieldsValue({ notes: data.comment });
        if (data.extra_symptoms) {
          extraSymptomsForm.setFieldsValue({ symptoms: data.extra_symptoms });
          setShowExtraSymptomsField(true);
        }
      },
    },
  );

  const { loading: postingSurvey, run: postSurvey } = useRequest(
    () => {
      const params = {
        ...survey,
        symptoms: Array.from(survey.symptoms),
        vaginal_discharges: Array.from(survey.vaginal_discharges),
        extra_symptoms: extraSymptomsValue,
        comment: freeNotes,
        menstruation_amount: isPeriodTrackingEnabled ? survey.menstruation_amount : 0,
      };
      if (isEditing) return ApiSymptomTrackingManager.patchSurvey(params);
      return ApiSymptomTrackingManager.postSurvey(params);
    },
    {
      manual: true,
      onSuccess: () => {
        clearCache('tracking.history');
        refresh();
        posthog.capture('tracking.survey.completed');
        if (!isEditing && !hasTrackingBeenUsed) {
          requestOptIn('first_tracking');
        }

        // Show reminder page if should ask for reminder and not editing
        if (shouldAskForReminder && !isEditing) {
          setShowReminderPage(true);
        } else {
          onClose();
        }
      },
      onError: e => {
        console.error(e);
      },
    },
  );

  function chooseGeneralState(value: number) {
    setSurvey(s => ({ ...s, general_state: value }));
    posthog.capture('tracking.survey.general_state_chosen');
  }

  function chooseMenstruationAmount(value: number) {
    setSurvey(s => ({ ...s, menstruation_amount: value }));
    posthog.capture('tracking.survey.menstruation_amount_chosen');
  }

  function toggleDeactivatePeriodTrackingWarning() {
    setShowSkipPeriodWarning(prev => !prev);
    posthog.capture('tracking.survey.no_menstruation_chosen');
  }

  function validateSkipPeriod() {
    setShouldAskForPeriod(false);
    chooseMenstruationAmount(0);
  }

  function handleReminderPageClose() {
    setShowReminderPage(false);
    onClose();
  }

  function handleReminderEnabled() {
    // Handle reminder enabled, then close
    handleReminderPageClose();
  }

  function handleReminderDeferred() {
    posthog.capture('tracking.reminder_setting_deferred');
    handleReminderPageClose();
    setShouldAskForReminder('false');
  }

  function handleModalClose() {
    setShowReminderPage(false);
    setShowExtraSymptomsField(false);
    setShowSkipPeriodWarning(false);
    onClose();
  }

  function choosePainAmount(value: number) {
    setSurvey(s => ({ ...s, pain_level: value }));
    posthog.capture('tracking.survey.pain_level_chosen');
  }

  function isSymptomSelected(symptom: string) {
    return survey.symptoms.has(symptom);
  }

  function toggleSymptom(symptom: string) {
    if (isSymptomSelected(symptom))
      setSurvey(oldSurvey => ({
        ...oldSurvey,
        symptoms: new Set([...oldSurvey.symptoms].filter(item => item !== symptom)),
      }));
    else setSurvey(oldSurvey => ({ ...oldSurvey, symptoms: new Set([...oldSurvey.symptoms, symptom]) }));
  }

  function renderSymptomButton(symptomKey: string, label: string) {
    return (
      <Button key={label} selected={isSymptomSelected(symptomKey)} onClick={() => toggleSymptom(symptomKey)}>
        <span>{label}</span>
        <div className={styles.symptomDot} style={{ backgroundColor: colorHash.hex(symptomKey) }} />
      </Button>
    );
  }

  function isVaginalDischargeSelected(vaginalDischarge: string) {
    return survey.vaginal_discharges.has(vaginalDischarge);
  }

  function toggleVaginalDischarge(vaginalDischarge: string) {
    if (isVaginalDischargeSelected(vaginalDischarge)) {
      setSurvey(oldSurvey => ({
        ...oldSurvey,
        vaginal_discharges: new Set([...oldSurvey.vaginal_discharges].filter(item => item !== vaginalDischarge)),
      }));
      return;
    }
    // "Aucune" is exclusive with the other options: picking it clears the rest, and picking any other
    // option clears "Aucune"
    if (vaginalDischarge === 'NONE') {
      setSurvey(oldSurvey => ({ ...oldSurvey, vaginal_discharges: new Set(['NONE']) }));
    } else {
      setSurvey(oldSurvey => ({
        ...oldSurvey,
        vaginal_discharges: new Set([...oldSurvey.vaginal_discharges].filter(item => item !== 'NONE')).add(
          vaginalDischarge,
        ),
      }));
    }
  }

  function renderVaginalDischargeButton(vaginalDischargeKey: string, label: string) {
    return (
      <Button
        key={label}
        selected={isVaginalDischargeSelected(vaginalDischargeKey)}
        onClick={() => toggleVaginalDischarge(vaginalDischargeKey)}
      >
        <span>{label}</span>
        {vaginalDischargeKey === 'NONE' ? (
          <CloseOutlined aria-hidden style={{ fontSize: 12, color: VAGINAL_DISCHARGE_COLORS.NONE }} />
        ) : (
          <div
            className={styles.symptomDot}
            style={{ backgroundColor: VAGINAL_DISCHARGE_COLORS[vaginalDischargeKey] }}
          />
        )}
      </Button>
    );
  }

  function chooseSexualActivity(value: string) {
    setSurvey(s => ({ ...s, sexual_activity: value }));
    posthog.capture('tracking.survey.sexual_activity_chosen');
  }

  function renderSexualActivityButton(value: string, label: string) {
    const icon =
      value === 'UNPROTECTED' ? (
        <CaretDownOutlined style={{ color: SEXUAL_ACTIVITY_COLORS.UNPROTECTED }} />
      ) : value === 'PROTECTED' ? (
        <CaretUpOutlined style={{ color: SEXUAL_ACTIVITY_COLORS.PROTECTED }} />
      ) : (
        <CloseOutlined style={{ color: SEXUAL_ACTIVITY_COLORS.NONE, fontSize: 12, marginTop: 4 }} />
      );
    return (
      <Button
        key={value}
        onClick={() => chooseSexualActivity(value)}
        selected={survey.sexual_activity === value}
        style={{ gap: 0 }}
      >
        <span>{label}</span>
        <div style={{ textAlign: 'center' }}>{icon}</div>
      </Button>
    );
  }

  function renderGeneralStateButton(value: number, label: string) {
    const selected = survey.general_state;
    return (
      <Button key={label} onClick={() => chooseGeneralState(value)} selected={selected === value}>
        {value > 2 && (
          <SmileFilled key={`${label}-good`} style={{ color: colors.dataViz[label as keyof typeof symptomSeverity] }} />
        )}
        {value === 2 && (
          <MehFilled
            key={`${label}-average`}
            style={{ color: colors.dataViz[label as keyof typeof symptomSeverity] }}
          />
        )}
        {value < 2 && (
          <FrownFilled key={`${label}-bad`} style={{ color: colors.dataViz[label as keyof typeof symptomSeverity] }} />
        )}
        <span>{t(`symptomTracking.survey.generalState.${label}`)}</span>
      </Button>
    );
  }

  function renderPainLevelButton(value: number, label: string) {
    const selected = survey.pain_level;
    return (
      <Button key={label} onClick={() => choosePainAmount(value)} selected={selected === value}>
        {value === 0 && (
          <Signal0 width={20} height={20} key={`${label}-thunderbolt`} stroke={colors.dataViz.veryGood} />
        )}
        {value === 1 && <Signal1 width={20} height={20} key={`${label}-thunderbolt`} stroke={colors.dataViz.good} />}
        {value === 2 && <Signal2 width={20} height={20} key={`${label}-thunderbolt`} stroke={colors.dataViz.average} />}
        {value === 3 && <Signal3 width={20} height={20} key={`${label}-thunderbolt`} stroke={colors.dataViz.bad} />}

        {value === 4 && <ThunderboltFilled key={`${label}-thunderbolt`} style={{ color: colors.dataViz.veryBad }} />}
        <span>{t(`symptomTracking.survey.pain.${label}`)}</span>
      </Button>
    );
  }

  function renderMenstruationAmountButton(value: number, label: string) {
    const selected = survey.menstruation_amount;
    return (
      <Button key={label} onClick={() => chooseMenstruationAmount(value)} selected={selected === value}>
        <div style={{ display: 'flex', gap: 2, marginBottom: 4 }}>
          {Array.from({ length: value }).map((_, index) => (
            <Drop fill={colors.dataViz.veryBad} key={`red-drop-${label}-${index}`} style={{ width: 12, height: 12 }} />
          ))}
          {Array.from({ length: 4 - value }).map((_, index) => (
            <Drop fill={colors.text.disabled} key={`gray-drop-${label}-${index}`} style={{ width: 12, height: 12 }} />
          ))}
        </div>
        <span>{t(`symptomTracking.survey.menstruation.${label}`)}</span>
      </Button>
    );
  }

  useEffect(() => {
    if (isEditing && open) loadSurvey();
  }, [isEditing, open, loadSurvey]);

  if (loadingShouldAskForPeriod || loadingReminderTime) {
    return (
      <Modal open={open} onCancel={onClose} footer={null} title={t('symptomTracking.survey.loading')} width={600}>
        <div className={styles.spinnerContainer}>
          <Spin size="large" />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={handleModalClose}
      footer={null}
      width={600}
      styles={{
        body: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '80vh',
        },
      }}
    >
      {isEditing && isLoadingSurveyData ? (
        <div className={styles.spinnerContainer}>
          <Spin size="large" />
        </div>
      ) : showReminderPage ? (
        /* Reminder Page */
        <>
          <div className={styles.modalContent}>
            <div className={styles.reminderSection}>
              <div className={styles.surveySection}>
                <Title level={5}>{t('symptomTracking.survey.reminder.question')}</Title>
              </div>
              <p>{t('symptomTracking.survey.reminder.explain')}</p>
              <div className={styles.answersContainer} style={{ marginTop: 16 }}>
                <SymptomTrackingReminder
                  mode="callToAction"
                  onReminderEnabled={handleReminderEnabled}
                  hidePeriodReminder={true}
                />
                <Button key="reminder-no-thanks" size="lg" onClick={handleReminderDeferred}>
                  {t('symptomTracking.survey.reminder.noThanks')}
                </Button>
              </div>
            </div>
          </div>
        </>
      ) : (
        /* Survey Page */
        <>
          <div className={styles.modalContent}>
            {/* Period Section - Moved before General State */}
            {isPeriodTrackingEnabled && (
              <>
                <div className={styles.surveySection}>
                  <Title level={3}>{date.format('LL')}</Title>
                  {(() => {
                    const fertilityStatus = getFertilityStatusForDay(date);
                    if (!fertilityStatus) return null;
                    const dotClass =
                      fertilityStatus.type === 'ovulation'
                        ? fertilityStatus.hollow
                          ? styles.fertilityDotOvulationHollow
                          : styles.fertilityDotOvulationFilled
                        : fertilityStatus.hollow
                          ? styles.fertilityDotFertileHollow
                          : styles.fertilityDotFertileFilled;
                    const label =
                      fertilityStatus.type === 'ovulation'
                        ? fertilityStatus.hollow
                          ? t('symptomTracking.survey.fertility.possibleOvulationDay')
                          : t('symptomTracking.survey.fertility.ovulationDay')
                        : fertilityStatus.hollow
                          ? t('symptomTracking.survey.fertility.possibleFertilityDay')
                          : t('symptomTracking.survey.fertility.fertilityDay');
                    return (
                      <>
                        <div className={styles.fertilityStatusRow}>
                          <span className={dotClass} />
                          <span>{label}</span>
                        </div>
                        {fertilityStatus.hollow && (
                          <Alert
                            type="warning"
                            showIcon
                            message={t('symptomTracking.survey.fertility.predictionWarning')}
                            style={{ marginBottom: 8 }}
                          />
                        )}
                      </>
                    );
                  })()}
                  <Title level={5}>
                    {QUESTION_ICONS.period} {t('symptomTracking.survey.menstruation.periodQuestion')}
                  </Title>
                </div>
                <div className={styles.horizontalAnswersWrapper}>
                  <div className={styles.horizontalAnswersContainer}>
                    {renderMenstruationAmountButton(0, 'none')}
                    {renderMenstruationAmountButton(1, 'low')}
                    {renderMenstruationAmountButton(2, 'medium')}
                    {renderMenstruationAmountButton(3, 'high')}
                    {renderMenstruationAmountButton(4, 'veryHigh')}
                  </div>
                </div>
                <div>
                  <Button
                    variant="link"
                    key="period-no-period"
                    onClick={toggleDeactivatePeriodTrackingWarning}
                    style={{ marginTop: 8, padding: 0 }}
                  >
                    <CloseOutlined />
                    <span>{t('symptomTracking.survey.menstruation.noPeriod')}</span>
                  </Button>
                  {showSkipPeriodWarning && (
                    <p className={styles.infoText}>
                      <WarningFilled style={{ color: colors.dataViz.bad, marginRight: 4 }} />
                      <Trans
                        i18nKey="symptomTracking.survey.menstruation.skipPeriodWarning"
                        components={{ b: <b /> }}
                      />
                      <br />
                      <Button
                        variant="highlight"
                        key="period-no-period-understood"
                        onClick={() => validateSkipPeriod()}
                      >
                        <CheckOutlined />
                        <span>{t('symptomTracking.survey.menstruation.understood')}</span>
                      </Button>
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Question selector */}
            <Collapse
              activeKey={categoriesOpen ? ['categories'] : []}
              onChange={keys => {
                const isOpen = (Array.isArray(keys) ? keys : [keys]).includes('categories');
                setCategoriesOpen(isOpen);
              }}
              className={styles.collapseCategories}
              expandIcon={({ isActive }) => (isActive ? <CloseOutlined /> : <RightOutlined />)}
              style={{
                borderRadius: radius.lg,
                margin: '8px 0',
                border: 'none',
                backgroundColor: colors.surface.page,
              }}
              styles={{
                header: {
                  backgroundColor: colors.surface.page,
                  borderRadius: radius.lg,
                  borderBottom: 'none',
                  // antd's default header has no horizontal padding at all, so
                  // the eye icon and arrow sat flush against — and visually
                  // spilled past — the rounded corners once the header itself
                  // became a pill shape.
                  paddingInline: 12,
                },
                body: { padding: '0 12px 12px', backgroundColor: colors.surface.page, borderTop: 'none' },
              }}
              items={[
                {
                  key: 'categories',
                  label: (
                    <>
                      <EyeOutlined /> Catégories suivies
                    </>
                  ),
                  children: (
                    <>
                      <div style={{ fontSize: 12, color: colors.text.tertiary, marginBottom: 8, fontWeight: 'normal' }}>
                        Personnalisez votre suivi en activant ou désactivant des questions.
                      </div>
                      <div className={styles.questionSelectorWrapper}>
                        <div className={styles.questionSelectorContainer}>
                          {questionOptions.map(option => (
                            <Button
                              key={option.value}
                              selected={visibleQuestions.includes(option.value)}
                              onClick={e => {
                                e.currentTarget.scrollIntoView({
                                  behavior: 'smooth',
                                  block: 'nearest',
                                  inline: 'center',
                                });
                                toggleQuestion(option.value);
                              }}
                            >
                              {option.icon} {option.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </>
                  ),
                },
              ]}
            />

            {/* General State Section */}
            {visibleQuestions.includes('generalState') && (
              <>
                <div className={styles.surveySection}>
                  <Title level={5}>
                    {QUESTION_ICONS.generalState} {t('symptomTracking.survey.howAreYouToday')}
                  </Title>
                </div>
                <div className={styles.horizontalAnswersWrapper}>
                  <div className={styles.horizontalAnswersContainer}>
                    {renderGeneralStateButton(4, 'veryGood')}
                    {renderGeneralStateButton(3, 'good')}
                    {renderGeneralStateButton(2, 'average')}
                    {renderGeneralStateButton(1, 'bad')}
                    {renderGeneralStateButton(0, 'veryBad')}
                  </div>
                </div>
              </>
            )}

            {/* Pain Section */}
            {visibleQuestions.includes('pain') && (
              <>
                <div className={styles.surveySection}>
                  <Title level={5}>
                    {QUESTION_ICONS.pain} {t('symptomTracking.survey.pain.painQuestion')}
                  </Title>
                </div>
                <div className={styles.horizontalAnswersWrapper}>
                  <div className={styles.horizontalAnswersContainer}>
                    {renderPainLevelButton(0, 'none')}
                    {renderPainLevelButton(1, 'light')}
                    {renderPainLevelButton(2, 'moderate')}
                    {renderPainLevelButton(3, 'strong')}
                    {renderPainLevelButton(4, 'intense')}
                  </div>
                </div>
              </>
            )}

            {/* Symptoms Section */}
            {visibleQuestions.includes('symptoms') && (
              <>
                <div className={styles.surveySection}>
                  <Title level={5}>
                    {QUESTION_ICONS.symptoms} {t('symptomTracking.survey.symptoms.otherSymptoms')}
                  </Title>
                </div>
                <div className={styles.horizontalAnswersWrapper}>
                  <div className={styles.twoRowsHorizontalAnswersContainer}>
                    {renderSymptomButton('FATIGUE', t('symptomTracking.survey.symptoms.FATIGUE'))}
                    {renderSymptomButton('DIGESTIVE', t('symptomTracking.survey.symptoms.DIGESTIVE'))}
                    {renderSymptomButton('BELLY_CRAMP', t('symptomTracking.survey.symptoms.BELLY_CRAMP'))}
                    {renderSymptomButton('MOOD', t('symptomTracking.survey.symptoms.MOOD'))}
                    {renderSymptomButton('BACK_PAIN', t('symptomTracking.survey.symptoms.BACK_PAIN'))}
                    {renderSymptomButton('HEADACHE', t('symptomTracking.survey.symptoms.HEADACHE'))}
                    {renderSymptomButton('NAUSEA', t('symptomTracking.survey.symptoms.NAUSEA'))}
                    {renderSymptomButton('BLOATING', t('symptomTracking.survey.symptoms.BLOATING'))}
                    {renderSymptomButton('DIZZINESS', t('symptomTracking.survey.symptoms.DIZZINESS'))}
                    {renderSymptomButton('URINARY', t('symptomTracking.survey.symptoms.URINARY'))}
                    {renderSymptomButton('BLEEDING', t('symptomTracking.survey.symptoms.BLEEDING'))}
                    {renderSymptomButton('SEXUAL', t('symptomTracking.survey.symptoms.SEXUAL'))}
                    {renderSymptomButton('MENTAL', t('symptomTracking.survey.symptoms.MENTAL'))}
                  </div>
                </div>
                {showExtraSymptomsField && (
                  <Form form={extraSymptomsForm} className={styles.freeTextFieldForm}>
                    <Form.Item name="symptoms">
                      <Input.TextArea
                        style={{ marginTop: 8 }}
                        variant="borderless"
                        autoCorrect="on"
                        placeholder={t('symptomTracking.survey.symptoms.otherSymptomsDescription')}
                      />
                    </Form.Item>
                  </Form>
                )}
                {!showExtraSymptomsField && (
                  <Button
                    key="other-symptoms"
                    icon={<PlusOutlined />}
                    style={{ marginTop: 8 }}
                    onClick={() => {
                      setShowExtraSymptomsField(true);
                      posthog.capture('tracking.survey.symptoms_chosen');
                    }}
                  >
                    {t('symptomTracking.survey.symptoms.other')}
                  </Button>
                )}
              </>
            )}

            {/* Vaginal Discharges Section */}
            {visibleQuestions.includes('vaginalDischarges') && (
              <>
                <div className={styles.surveySection}>
                  <Title level={5}>
                    {QUESTION_ICONS.vaginalDischarges} {t('symptomTracking.survey.vaginalDischarges.title')}
                  </Title>
                </div>
                <div className={styles.horizontalAnswersWrapper}>
                  <div className={styles.horizontalAnswersContainer}>
                    {renderVaginalDischargeButton('NONE', t('symptomTracking.survey.vaginalDischarges.NONE'))}
                    {renderVaginalDischargeButton('SPOTTING', t('symptomTracking.survey.vaginalDischarges.SPOTTING'))}
                    {renderVaginalDischargeButton('STICKY', t('symptomTracking.survey.vaginalDischarges.STICKY'))}
                    {renderVaginalDischargeButton('CREAMY', t('symptomTracking.survey.vaginalDischarges.CREAMY'))}
                    {renderVaginalDischargeButton('EGG_WHITE', t('symptomTracking.survey.vaginalDischarges.EGG_WHITE'))}
                    {renderVaginalDischargeButton('UNUSUAL', t('symptomTracking.survey.vaginalDischarges.UNUSUAL'))}
                  </div>
                </div>
              </>
            )}

            {/* Sexual Activity Section */}
            {visibleQuestions.includes('sexualActivity') && (
              <>
                <div className={styles.surveySection}>
                  <Title level={5}>
                    {QUESTION_ICONS.sexualActivity} {t('symptomTracking.survey.sexualActivity.title')}
                  </Title>
                </div>
                <div className={styles.horizontalAnswersWrapper}>
                  <div className={styles.horizontalAnswersContainer}>
                    {renderSexualActivityButton('NONE', t('symptomTracking.survey.sexualActivity.NONE'))}
                    {renderSexualActivityButton('PROTECTED', t('symptomTracking.survey.sexualActivity.PROTECTED'))}
                    {renderSexualActivityButton('UNPROTECTED', t('symptomTracking.survey.sexualActivity.UNPROTECTED'))}
                  </div>
                </div>
              </>
            )}

            {/* Body Temperature Section */}
            {visibleQuestions.includes('bodyTemperature') && (
              <>
                <div className={styles.surveySection}>
                  <Title level={5}>
                    {QUESTION_ICONS.bodyTemperature} {t('symptomTracking.survey.bodyTemperature.title')}
                  </Title>
                </div>
                <div className={styles.answersContainer}>
                  <InputNumber
                    aria-label={t('symptomTracking.survey.bodyTemperature.title')}
                    value={survey.body_temperature}
                    onChange={value => setSurvey(s => ({ ...s, body_temperature: value }))}
                    suffix="°C"
                    step={0.1}
                    min={30}
                    max={45}
                  />
                </div>
              </>
            )}

            {/* Notes Section */}
            {visibleQuestions.includes('notes') && (
              <>
                <div className={styles.surveySection}>
                  <Title level={5}>
                    {QUESTION_ICONS.notes} {t('symptomTracking.survey.addComment')}
                  </Title>
                </div>
                <div className={styles.answersContainer}>
                  <Form form={freeNotesForm} className={styles.freeTextFieldForm}>
                    <Form.Item name="notes">
                      <Input.TextArea
                        variant="borderless"
                        placeholder={t('symptomTracking.survey.freeTextDescription')}
                      />
                    </Form.Item>
                  </Form>
                </div>
              </>
            )}

            {!isPeriodTrackingEnabled && (
              <>
                <div className={styles.surveySection}>
                  <Title level={5}>
                    {QUESTION_ICONS.period} {t('symptomTracking.survey.menstruation.periodQuestion')}
                  </Title>

                  <div className={styles.noPeriodsContainer}>
                    <p>{t('symptomTracking.tracking.disabled')}</p>
                    <Button
                      variant="link"
                      onClick={() => {
                        setShouldAskForPeriod(true);
                        posthog.capture('tracking.period_tracking_reactivated');
                      }}
                    >
                      {t('symptomTracking.tracking.reactivate')}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Fixed Save Button */}
          <div className={styles.fixedBottomButton}>
            <Button variant="primary" size="lg" block loading={postingSurvey} onClick={postSurvey}>
              {t('symptomTracking.survey.saveFollowUp')}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
